<template>
    <Popper :arrow :placement :show :hover :locked :close-delay="delay" class="ds-tooltip" z-index="600">
        <button
            v-if="!hasCustomTrigger && hasTrigger"
            type="button"
            class="ds-tooltip__link"
            :aria-label="ariaLabelValue"
        >
            <DsIcon :is-in-storybook="props.isInStorybook" :name="props.icon" :width="iconWidth" :height="iconHeight" />
        </button>
        <slot name="custom-trigger" v-else-if="hasTrigger"></slot>
        <template #content="{ close }">
            <DsButton
                v-if="hasCloseButton"
                type="primary"
                icon-right="cross"
                size="sm"
                @click="close"
                class="ds-tooltip__close-button"
            />
            <div class="ds-tooltip__content" role="tooltip" ref="popperRef">
                <slot></slot>
            </div>
        </template>
    </Popper>
</template>

<script setup lang="ts">
import { computed, ref, useSlots } from "vue";
import Popper from "vue3-popper";
import DsIcon from "../icon/ds-icon.vue";
import type { SvgIcons } from "../icon/types";
import type { Placement } from "../types/popper";
import DsButton from "../button/ds-button.vue";

const popperRef = ref<HTMLElement>(null);

defineExpose({
    popperRef,
});

const props = withDefaults(
    defineProps<{
        /**
         * Delay for autoclose
         */
        delay?: number;
        /**
         * Placement
         */
        placement?: Placement;
        /**
         * Set icon
         */
        icon?: SvgIcons;
        /**
         * Use different image path for storybook
         */
        isInStorybook?: boolean;
        /**
         * Icon width
         */
        iconWidth?: number;
        /**
         * Icon height
         */
        iconHeight?: number;
        /**
         * Manually show/hide tooltip
         */
        show?: boolean;
        /**
         * Show/hide arrow
         */
        arrow?: boolean;
        /**
         * Show tooltip on hover
         */
        hover?: boolean;
        /**
         * Locks tooltip placement
         */
        locked?: boolean;
        /**
         * Display tooltip as popup on hover/click or inline
         */
        hasTrigger?: boolean;
        /**
         * Display close button
         */
        hasCloseButton?: boolean;
        /**
         * Aria label for button
         */
        ariaLabelValue?: string;
    }>(),
    {
        delay: 300,
        placement: "top",
        icon: "info",
        isInStorybook: false,
        show: undefined,
        arrow: true,
        hover: true,
        locked: false,
        hasTrigger: true,
        hasCloseButton: false,
    }
);

const slots = useSlots();
const hasCustomTrigger = computed(() => !!slots["custom-trigger"]);
</script>
